import React, { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import MuiDialog, { type DialogProps } from '@mui/material/Dialog';
import MuiDrawer, { type DrawerProps } from '@mui/material/Drawer';
import MuiMenu, { type MenuProps } from '@mui/material/Menu';
import MuiModal, { type ModalProps } from '@mui/material/Modal';
import MuiPopover, { type PopoverProps } from '@mui/material/Popover';
import MuiSelect, { type SelectProps } from '@mui/material/Select';
import MuiTextField, { type TextFieldProps } from '@mui/material/TextField';

const owners = new Set<symbol>();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
const snapshot = () => owners.size > 0;
const notify = () => listeners.forEach(listener => listener());

/** The app-wide gate owns the lock, including status uncertainty and cleanup. */
export function usePublishChatFeedbackLock(locked: boolean) {
  const owner = useRef(Symbol('chat-feedback-gate'));
  useLayoutEffect(() => {
    if (!locked) return;
    owners.add(owner.current);
    notify();
    const token = owner.current;
    return () => { owners.delete(token); notify(); };
  }, [locked]);
}

export function useChatFeedbackLock() {
  return useSyncExternalStore(subscribe, snapshot, () => false);
}

// Closing the actual MUI open prop releases its focus trap, sibling aria hiding
// and scroll lock. Keep suspended children mounted so unfinished dialog inputs
// survive. The owning component's open state resumes after feedback saves.
export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>((props, ref) => {
  const locked = useChatFeedbackLock();
  return <MuiDialog {...props} ref={ref} open={props.open && !locked}
    {...(locked ? { keepMounted: props.keepMounted || props.open, closeAfterTransition: false,
      disableRestoreFocus: true, transitionDuration: 0 } : {})} />;
});

export const Modal = React.forwardRef<HTMLDivElement, ModalProps>((props, ref) => {
  const locked = useChatFeedbackLock();
  return <MuiModal {...props} ref={ref} open={props.open && !locked}
    {...(locked ? { keepMounted: props.keepMounted || props.open, closeAfterTransition: false,
      disableRestoreFocus: true } : {})} />;
});

export const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>((props, ref) => {
  const locked = useChatFeedbackLock() && (!props.variant || props.variant === 'temporary');
  return <MuiDrawer {...props} ref={ref} open={props.open && !locked}
    {...(locked ? { transitionDuration: 0, ModalProps: { ...props.ModalProps,
      keepMounted: props.ModalProps?.keepMounted || props.open, closeAfterTransition: false,
      disableRestoreFocus: true } } : {})} />;
});

export const Menu = React.forwardRef<HTMLDivElement, MenuProps>((props, ref) => {
  const locked = useChatFeedbackLock();
  return <MuiMenu {...props} ref={ref} open={props.open && !locked}
    {...(locked ? { keepMounted: props.keepMounted || props.open, closeAfterTransition: false,
      disableRestoreFocus: true, transitionDuration: 0 } : {})} />;
});

export const Popover = React.forwardRef<HTMLDivElement, PopoverProps>((props, ref) => {
  const locked = useChatFeedbackLock();
  return <MuiPopover {...props} ref={ref} open={props.open && !locked}
    {...(locked ? { keepMounted: props.keepMounted || props.open, closeAfterTransition: false,
      disableRestoreFocus: true, transitionDuration: 0 } : {})} />;
});

function suspendedMenu(props?: Partial<MenuProps>): Partial<MenuProps> {
  return { ...props, open: false, keepMounted: true, closeAfterTransition: false,
    disableRestoreFocus: true, transitionDuration: 0 };
}

// Select owns uncontrolled menu-open state internally. Override its MenuProps
// rather than changing Select's controlledness, which MUI fixes on first mount.
export const Select = React.forwardRef<HTMLDivElement, SelectProps>((props, ref) => {
  const locked = useChatFeedbackLock();
  return <MuiSelect {...props} ref={ref}
    {...(locked ? { MenuProps: suspendedMenu(props.MenuProps) } : {})} />;
}) as unknown as typeof MuiSelect;
// FormControl recognizes Select children by this MUI integration marker.
(Select as typeof MuiSelect & { muiName: string }).muiName = 'Select';

export const TextField = React.forwardRef<HTMLDivElement, TextFieldProps>((props, ref) => {
  const locked = useChatFeedbackLock();
  return <MuiTextField {...props} ref={ref}
    {...(locked && props.select ? { SelectProps: { ...props.SelectProps,
      MenuProps: suspendedMenu(props.SelectProps?.MenuProps) } } : {})} />;
}) as typeof MuiTextField;
